import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Film, Download, Star, Flame } from "lucide-react";
import { getPersonClips, personClipsDownloadUrl, setGlobalFollow, mediaUrl } from "../lib/api";
import { ClipCard } from "../components/ClipCard";
import { ProDownloadLink } from "../components/UpgradeModal";
import { toast } from "sonner";

export default function PersonClips({ person, followName, onBack }) {
  const [clips, setClips] = useState([]);
  const [info, setInfo] = useState(person);
  const [loading, setLoading] = useState(true);
  const [reelSize, setReelSize] = useState(5);
  const [minScore, setMinScore] = useState(0);
  const [following, setFollowing] = useState(
    (followName || "").toLowerCase() === (person.name || "").toLowerCase()
  );

  const toggleFollow = async () => {
    try {
      const g = await setGlobalFollow(following ? "" : info.name);
      const now = (g.follow_name || "").toLowerCase() === (info.name || "").toLowerCase();
      setFollowing(now);
      toast.success(now ? `Now auto-following ${info.name} in every show` : `Stopped auto-following ${info.name}`);
    } catch {
      toast.error("Could not update default follow");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPersonClips(person.id);
      setClips(data.clips || []);
      setInfo(data.person || person);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [person]);

  useEffect(() => { load(); }, [load]);

  const visible = clips.filter((c) => (c.funny_score || 0) >= minScore);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="person-clips-view" className="space-y-8">
      <div className="flex items-center gap-4 border-b border-border pb-6">
        <button
          data-testid="person-clips-back"
          onClick={onBack}
          className="grid h-11 w-11 shrink-0 place-items-center border border-border text-muted-foreground transition-colors hover:border-accent hover:text-accent"
          title="Back to roster"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {info.avatar ? (
          <img src={mediaUrl(info.avatar)} alt={info.name} className="h-14 w-14 shrink-0 rounded-full border border-border object-cover" />
        ) : (
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full border border-border bg-secondary font-display text-xl text-muted-foreground">
            {(info.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">Every clip they're in</p>
          <h2 className="font-display text-3xl tracking-wide text-white">{info.name}</h2>
        </div>
        <button
          data-testid="person-follow-toggle"
          onClick={toggleFollow}
          title={following ? "Default follow — click to unset" : "Follow this person in every show"}
          className={`ml-auto flex h-11 shrink-0 items-center gap-2 border px-4 font-mono text-[11px] uppercase tracking-widest transition-colors ${
            following ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent hover:text-accent"
          }`}
        >
          <Star className="h-4 w-4" fill={following ? "#CCFF00" : "none"} />
          {following ? "Following" : "Follow in every show"}
        </button>
        {clips.length > 0 && (
          <div className="flex shrink-0 items-center gap-3">
            <div className="flex items-center border border-border" data-testid="reel-size-picker">
              {[3, 5, 10].map((n) => (
                <button
                  key={n}
                  data-testid={`reel-size-${n}`}
                  onClick={() => setReelSize(n)}
                  className={`px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                    reelSize === n ? "bg-secondary text-white" : "text-muted-foreground hover:text-white"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <ProDownloadLink
              data-testid="person-download-top"
              reason="Per-guest reel download is a Pro feature. Upgrade to export highlight reels."
              href={personClipsDownloadUrl(person.id, { limit: reelSize, kind: "video", min_score: minScore })}
              className="flex h-11 items-center gap-2 bg-accent px-5 text-xs font-semibold uppercase tracking-[0.15em] text-accent-foreground transition-colors hover:bg-accent/90"
            >
              <Download className="h-4 w-4" /> Download Top {Math.min(reelSize, visible.length)}
            </ProDownloadLink>
          </div>
        )}
      </div>

      {clips.length > 0 && (
        <div className="flex items-center gap-3" data-testid="person-score-filter">
          <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            <Flame className="h-3.5 w-3.5 text-primary" /> min score {minScore}
          </span>
          <input
            data-testid="person-minscore-slider"
            type="range" min="0" max="95" step="5"
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value))}
            className="w-56 accent-[#CCFF00]"
          />
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : clips.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center" data-testid="person-clips-empty">
          <Film className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
          <p className="font-mono text-sm text-muted-foreground">
            No clips found for {info.name} yet. They show up here once a mined clip's active-speaker track lands on their face.
          </p>
        </div>
      ) : (
        <>
          <p data-testid="person-clips-count" className="font-mono text-xs text-muted-foreground">
            {visible.length} of {clips.length} clip{clips.length === 1 ? "" : "s"} featuring {info.name}
            {minScore > 0 && <span className="text-accent"> · score ≥ {minScore}</span>}
          </p>
          {visible.length === 0 ? (
            <p className="py-16 text-center font-mono text-sm text-muted-foreground">No clips at score ≥ {minScore} — lower the minimum.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {visible.map((c, i) => (
                <ClipCard key={c.id} clip={c} show={c.show} index={i} onUpdated={load} sourceLabel={c.show?.title} />
              ))}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}
